import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';
import { SortableTable, type SortableColumn } from '../ui/SortableTable';
import { listAccessRequests, updateAccessRequest } from '../../utils/database';
import { friendlyTime } from '../../utils/friendlyTime';
import type { AccessRequest } from '../../types';
const STATUS_OPTIONS = ['', 'pending', 'approved', 'rejected'] as const;
function StatusBadge({
  status
}: {
  status: AccessRequest['status'];
}) {
  const styles: Record<string, string> = {
    pending: 'bg-amber-100 text-amber-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
  };
  return <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] ?? 'bg-slate-100 text-slate-700'}`} data-id="element-242" data-mp="1w7a3uzk0h4v4">
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>;
}
function truncate(str: unknown, max = 60): string {
  if (str === null || str === undefined) return '—';
  const s = typeof str === 'string' ? str : JSON.stringify(str);
  return s.length > max ? s.substring(0, max) + '…' : s;
}
export function AccessRequestsTab() {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [confirmAction, setConfirmAction] = useState<{
    requestId: string;
    action: 'approved' | 'rejected';
    email: string;
  } | null>(null);
  const [processing, setProcessing] = useState(false);
  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listAccessRequests(filterStatus ? {
        status: filterStatus
      } : undefined);
      setRequests(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load access requests');
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);
  useEffect(() => {
    void loadRequests();
  }, [loadRequests]);
  const handleConfirm = async () => {
    if (!confirmAction) return;
    setProcessing(true);
    try {
      await updateAccessRequest(confirmAction.requestId, confirmAction.action, 'admin');
      setConfirmAction(null);
      await loadRequests();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update request');
    } finally {
      setProcessing(false);
    }
  };
  const columns: SortableColumn<AccessRequest>[] = [{
    key: 'displayName',
    label: 'Requester',
    width: '22%',
    render: row => <div data-id="element-243" data-mp="w2hgqqcgl6h4">
          <div className="text-sm font-medium text-slate-900" data-id="element-244" data-mp="w1zend347y1a">{row.displayName || '—'}</div>
          <div className="text-xs text-slate-500" data-id="element-245" data-mp="112venfkcj01f">{row.email}</div>
        </div>,
    sortValue: row => row.displayName || row.email
  }, {
    key: 'reason',
    label: 'Reason',
    width: '30%',
    render: row => <span className="text-xs text-slate-600" title={row.reason} data-id="element-246" data-mp="2citvw6pogsdq">
          {truncate(row.reason, 80)}
        </span>,
    sortValue: row => row.reason
  }, {
    key: 'requestedAt',
    label: 'Requested',
    width: '18%',
    render: row => <span className="text-xs text-slate-500 whitespace-nowrap" data-id="element-247" data-mp="3n7nlmwey75ca">{friendlyTime(row.requestedAt)}</span>,
    sortValue: row => row.requestedAt
  }, {
    key: 'status',
    label: 'Status',
    width: '12%',
    render: row => <StatusBadge status={row.status} data-id="element-248" data-mp="399ql5oxdiesc" />,
    sortValue: row => row.status
  }, {
    key: 'actions',
    label: 'Actions',
    width: '18%',
    render: row => row.status === 'pending' ? <div className="flex items-center gap-1.5" data-id="element-249" data-mp="2zc2qeyzrp7a6">
            <button onClick={() => setConfirmAction({
        requestId: row.requestId,
        action: 'approved',
        email: row.email
      })} className="px-2.5 py-1 rounded-md bg-green-600 text-white text-xs font-medium hover:bg-green-700 transition-colors" data-id="element-250" data-mp="2zddqoou07wwj">
              Approve
            </button>
            <button onClick={() => setConfirmAction({
        requestId: row.requestId,
        action: 'rejected',
        email: row.email
      })} className="px-2.5 py-1 rounded-md bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition-colors" data-id="element-251" data-mp="1ajfah5evyipe">
              Reject
            </button>
          </div> : row.reviewedBy ? <span className="text-xs text-slate-400" data-id="element-252" data-mp="msteici5oovr">by {row.reviewedBy}</span> : null
  }];
  return <div className="space-y-4" data-id="element-253" data-mp="rmnr6vzcg3lc">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-254" data-mp="cx9ilq3ldq70">
        <div data-id="element-255" data-mp="hurv1qgtinjr">
          <h3 className="text-base font-semibold text-slate-900" data-id="element-256" data-mp="hui0dpktvs7q">Access Requests</h3>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-257" data-mp="3vigev89g1hw">
            User access requests ({requests.length} total)
          </p>
        </div>
        <button onClick={() => void loadRequests()} disabled={loading} className="p-2 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors disabled:opacity-50" title="Refresh" data-id="element-258" data-mp="7vk9ifzxjmlk">
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} data-id="element-259" data-mp="3uedv1gbxh32l" />
        </button>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3" data-id="element-260" data-mp="3pehn56040rhr">
        <div data-id="element-261" data-mp="3phvrgjsc3aru">
          <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-262" data-mp="87fg22pyt5cs">Status</label>
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="px-3 py-1.5 rounded-md border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-id="element-263" data-mp="852vdhstogw1">
            <option value="" data-id="element-264" data-mp="d08op1ok0xu5">All statuses</option>
            {STATUS_OPTIONS.filter(Boolean).map(s => <option key={s} value={s} data-id="element-265" data-mp="qvss0orsv7gg">{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
        </div>
      </div>

      {/* Error */}
      {error && <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700" data-id="element-266" data-mp="3fsmz6i7iyrep">
          {error}
        </div>}

      {/* Table */}
      {loading && requests.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-267" data-mp="3opdrqkmvbo4j">
          Loading access requests...
        </div> : requests.length === 0 ? <div className="flex items-center justify-center py-12 text-slate-400 text-sm" data-id="element-268" data-mp="2b48epxsp6am">
          No access requests found.
        </div> : <SortableTable columns={columns} data={requests} rowKey={row => row.requestId} defaultSort={{
      key: 'requestedAt',
      dir: 'desc'
    }} data-id="element-269" data-mp="bctwblsgxn3n" />}

      {/* Confirmation Modal */}
      {confirmAction && <div className="fixed inset-0 z-50 flex items-center justify-center" style={{
      background: 'rgba(0,0,0,0.4)'
    }} onClick={() => !processing && setConfirmAction(null)} data-id="element-270" data-mp="2d5i488g24ypr">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()} data-id="element-271" data-mp="2allqnpgehhff">
            <h3 className="text-lg font-semibold text-slate-900" data-id="element-272" data-mp="20sxsordop7f4">
              {confirmAction.action === 'approved' ? 'Approve Access' : 'Reject Access'}
            </h3>
            <p className="mt-2 text-sm text-slate-600" data-id="element-273" data-mp="2ti4tjx0m293j">
              {confirmAction.action === 'approved' ? `Approve access for ${confirmAction.email}? They will receive an email with next steps.` : `Reject access for ${confirmAction.email}? They will be notified by email.`}
            </p>
            <div className="mt-4 flex items-center justify-end gap-2" data-id="element-274" data-mp="2fjdh0f1ecvhi">
              <button onClick={() => setConfirmAction(null)} disabled={processing} className="px-4 py-2 rounded-md border border-slate-300 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 transition-colors" data-id="element-275" data-mp="184saaba3ruig">
                Cancel
              </button>
              <button onClick={() => void handleConfirm()} disabled={processing} className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-60 transition-colors ${confirmAction.action === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`} data-id="element-276" data-mp="3882xoa1j0vma">
                {processing ? 'Processing…' : confirmAction.action === 'approved' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>}
    </div>;
}